/**
 * Where a panel lives and what each section is asking the owner to look at.
 *
 * Command is one screen in three primary rooms. The CONTROL CENTER is the working
 * surface - the conversation with Brunel, what is running, and the workforce
 * beside it. The ENGINE ROOM has three views: Live is the ship's state now -
 * machines, gauges, AI capacity, ship systems and the Machinist; History is the
 * read-only record of finished work, reports, conversations, plans and audit
 * evidence; and AI Sessions is the recorded session lifecycle. SETTINGS &
 * OPTIONS is the machinery: computers, rules,
 * projects and runtimes.
 *
 * There is no Workers page: a worker is read beside the conversation and
 * opened in its own window from there, not parked on a page of its own.
 *
 * Everything here is pure - the room passes its counts and its storage in - so
 * the rules can be tested without a browser.
 */

export type SectionId = 'control' | 'engine' | 'settings';
export type EngineRoomView = 'live' | 'history' | 'sessions';

export interface Section { readonly id: SectionId; readonly label: string }

export const SECTIONS: readonly Section[] = [
  { id: 'control', label: 'Control Center' },
  { id: 'engine', label: 'Engine Room' },
  { id: 'settings', label: 'Settings & Options' },
];

/** The section the owner lands on when nothing is remembered. */
export const DEFAULT_SECTION: SectionId = 'control';

export function isSectionId(value: unknown): value is SectionId {
  return typeof value === 'string' && SECTIONS.some((s) => s.id === value);
}

export function isEngineRoomView(value: unknown): value is EngineRoomView {
  return value === 'live' || value === 'history' || value === 'sessions';
}

export interface BadgeInput {
  /** Plans waiting for the owner's decision. */
  readonly proposedPlans: number;
  /** Approvals waiting for the owner's decision. */
  readonly pendingApprovals: number;
  /** Workers that cannot go on until the owner answers them. */
  readonly blockedWorkers: number;
  /** Computers that are not reporting in. */
  readonly nodesOutOfTouch: number;
  /**
   * Ship systems and AI lanes the Engine Room reports as failing, unavailable
   * or exhausted. These change what Brunel can safely do next, so they are the
   * owner's business; a machine merely working hard is not.
   */
  readonly engineRoomAlerts: number;
}

/**
 * The number beside each section name. A badge means "this needs you", never
 * "this has history rows in it". Engine Room History therefore does not add a
 * badge of its own. A blocked worker is asking the owner a question, and the
 * owner answers it where the workforce is, which is the Control Center.
 */
export function sectionBadges(input: BadgeInput): Readonly<Record<SectionId, number>> {
  return {
    control: input.proposedPlans + input.pendingApprovals + input.blockedWorkers,
    engine: input.engineRoomAlerts,
    settings: input.nodesOutOfTouch,
  };
}

/**
 * A panel that holds nothing and asks nothing is not worth the vertical space:
 * it stays out of the section until it has something to say. Panels the owner
 * acts in - the command composer, the forms in Settings - are never empty in
 * this sense and are never passed through here.
 */
export function showPanel(count: number, options?: { readonly alwaysShow?: boolean }): boolean {
  return Boolean(options?.alwaysShow) || count > 0;
}

/** The narrowest the workforce column may be dragged, in pixels. */
export const SIDE_MIN_PX = 300;

/**
 * The Worker floor and Command Center are peers in the Cockpit. Keep a dragged desktop
 * split inside a useful 40/60–60/40 range instead of letting either side collapse back
 * to the old command-dominant layout. On a desk too small for that range the
 * absolute readable minimum wins; the responsive stylesheet stacks the columns
 * before that becomes the normal working layout.
 */
export function clampSideWidth(px: number, containerPx: number): number {
  const narrowest = Math.max(SIDE_MIN_PX, containerPx * 0.4);
  const widest = Math.max(narrowest, containerPx * 0.6);
  return Math.round(Math.min(widest, Math.max(narrowest, px)));
}

/** The little of the owner's chosen layout that is worth surviving a reload. */
export interface Remembered {
  section: SectionId; engineRoomView: EngineRoomView; orgId: string | null; projectId: string | null;
  /** Width in pixels the owner dragged the workforce column to, or null for the default. */
  sideWidth: number | null;
  /**
   * Height in pixels the owner dragged the command box to, or null for the default.
   *
   * The box is for stating a goal, not for reading one back - the transcript above it is
   * where he reads, and this stays modest so that stays the bigger of the two on the screen.
   * Where he has dragged it taller for a longer goal, it is his, within a range that cannot
   * bury the Workers beneath it.
   */
  composerHeight: number | null;
  /** Height of the whole Command Center card, or null for its content-sized default. */
  commandHeight: number | null;
}

/** The command box, in pixels: how tall it opens, and how far it may be dragged. */
export const COMPOSER_MIN_PX = 96;
/**
 * It opens at the floor - about three lines, enough to state a goal without becoming the
 * screen. The owner reads far more here than he types; the transcript above this box is
 * where the room belongs, and the box only grows past this when he drags it there himself.
 */
export const COMPOSER_DEFAULT_PX = COMPOSER_MIN_PX;

/** The tallest the box may be: a share of the window, so it never swallows the Cockpit. */
export function clampComposerHeight(px: number, viewportPx: number): number {
  const ceiling = Math.max(COMPOSER_MIN_PX, Math.round(viewportPx * 0.42));
  return Math.round(Math.min(ceiling, Math.max(COMPOSER_MIN_PX, px)));
}

/** The Command Center itself can be compact or fill most of the remaining desk. */
export const COMMAND_CENTER_MIN_PX = 300;
export function clampCommandCenterHeight(px: number, viewportPx: number): number {
  const ceiling = Math.max(COMMAND_CENTER_MIN_PX, Math.round(viewportPx * 0.78));
  return Math.round(Math.min(ceiling, Math.max(COMMAND_CENTER_MIN_PX, px)));
}

const KEY = 'brunel.command.layout';

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The browser's own store, or nothing at all when it is unavailable or refused. */
export function browserStore(): StringStore | null {
  try {
    const store = (globalThis as { localStorage?: StringStore }).localStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** What was remembered last time, falling back to the defaults on anything unexpected. */
export function recallLayout(store: StringStore | null): Remembered {
  const fallback: Remembered = { section: DEFAULT_SECTION, engineRoomView: 'live', orgId: null, projectId: null, sideWidth: null, composerHeight: null, commandHeight: null };
  if (store === null) return fallback;
  let raw: string | null = null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return fallback;
  }
  if (raw === null) return fallback;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (typeof parsed !== 'object' || parsed === null) return fallback;
  const row = parsed as Record<string, unknown>;
  const sideWidth = row['sideWidth'];
  const composerHeight = row['composerHeight'];
  const commandHeight = row['commandHeight'];
  // #494 removed Monitor Center as a primary room. Preserve the owner's old
  // destination by translating that legacy section into Engine Room History.
  const legacyMonitor = row['section'] === 'monitor';
  const engineRoomView: EngineRoomView = legacyMonitor
    ? 'history'
    : (isEngineRoomView(row['engineRoomView']) ? row['engineRoomView'] : 'live');
  return {
    section: legacyMonitor ? 'engine' : (isSectionId(row['section']) ? row['section'] : DEFAULT_SECTION),
    engineRoomView,
    orgId: typeof row['orgId'] === 'string' ? row['orgId'] : null,
    projectId: typeof row['projectId'] === 'string' ? row['projectId'] : null,
    sideWidth: typeof sideWidth === 'number' && Number.isFinite(sideWidth) && sideWidth > 0 ? sideWidth : null,
    composerHeight: typeof composerHeight === 'number' && Number.isFinite(composerHeight) && composerHeight > 0
      ? composerHeight : null,
    commandHeight: typeof commandHeight === 'number' && Number.isFinite(commandHeight) && commandHeight > 0
      ? commandHeight : null,
  };
}

/** Remember the layout. A store that refuses to write is not an error worth showing. */
export function rememberLayout(store: StringStore | null, layout: Remembered): void {
  if (store === null) return;
  try {
    store.setItem(KEY, JSON.stringify(layout));
  } catch {
    /* a browser with storage turned off simply does not remember */
  }
}

/** The public Command Web root. A trailing slash is the same address. */
export function isCommandWebRoot(pathname: string): boolean {
  return pathname === '/' || pathname === '' || pathname === '/index.html';
}

/**
 * Opening the site root shows Control Center. A remembered room still applies
 * on any other path, and recallLayout itself is unchanged.
 */
export function landingSection(pathname: string, remembered: SectionId): SectionId {
  return isCommandWebRoot(pathname) ? DEFAULT_SECTION : remembered;
}

/** In-page Settings links. They are not a second section route. */
export function isSettingsAnchor(hash: string): boolean {
  return hash.startsWith('#settings-');
}

export interface SectionReveal {
  /** Replacement hash, including a blank string to clear one. Null keeps the current hash. */
  readonly hash: string | null;
  readonly scrollToTop: boolean;
}

/**
 * Choosing a room has to show that room. Control Center begins at the top of
 * the page, so a long Settings scroll would otherwise leave only the highlight.
 * A settings anchor is cleared. Any other hash, including the machine board, stays.
 */
export function revealChosenSection(hash: string): SectionReveal {
  return {
    hash: isSettingsAnchor(hash) ? '' : null,
    scrollToTop: true,
  };
}

/** On a root load, a leftover settings anchor would scroll the owner away from Control Center. */
export function landingReveal(pathname: string, hash: string): SectionReveal | null {
  if (!isCommandWebRoot(pathname) || !isSettingsAnchor(hash)) return null;
  return { hash: '', scrollToTop: true };
}

export interface SectionRevealWindow {
  readonly location: { readonly hash: string; readonly pathname: string; readonly search: string };
  readonly history: {
    readonly state: unknown;
    replaceState(state: unknown, title: string, url: string): void;
  };
  scrollTo(x: number, y: number): void;
}

/** Apply a reveal: clear a settings anchor, then scroll to the top of the room. */
export function applySectionReveal(target: SectionRevealWindow, reveal: SectionReveal): void {
  if (reveal.hash !== null && target.location.hash !== reveal.hash) {
    target.history.replaceState(
      target.history.state,
      '',
      `${target.location.pathname}${target.location.search}${reveal.hash}`,
    );
  }
  if (reveal.scrollToTop) target.scrollTo(0, 0);
}
